import create from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Voucher data structure with id, code, and title for tracking
 */
export type AppliedVoucher = {
  id: string;
  code: string;
  title: string;
};

type VoucherState = {
  appliedVouchers: AppliedVoucher[];
  pendingVouchers: string[];
  addVoucher: (id: string, code: string, title: string) => void;
  removeVoucher: (code: string) => void;
  removeVouchers: (codes: string[]) => void;
  clearVouchers: () => void;
  isVoucherApplied: (code: string) => boolean;
  getAppliedVoucher: () => AppliedVoucher | null;
  addPendingVoucher: (code: string) => void;
  removePendingVoucher: (code: string) => void;
  clearPendingVouchers: () => void;
  isPendingVoucher: (code: string) => boolean;
};

/**
 * Migrate old string[] format to new AppliedVoucher[] format
 */
function migrateVouchers(vouchers: unknown): AppliedVoucher[] {
  if (!Array.isArray(vouchers)) return [];

  return vouchers
    .map((v) => {
      // Already new format
      if (typeof v === 'object' && v !== null && 'id' in v && 'code' in v) {
        const voucher = v as Record<string, unknown>;
        return {
          id: String(voucher.id),
          code: String(voucher.code),
          title: typeof voucher.title === 'string' ? voucher.title : String(voucher.code),
        };
      }
      // Old format (string) - skip as we don't have id
      return null;
    })
    .filter((v): v is AppliedVoucher => v !== null);
}

export const useVoucherStore = create<VoucherState>()(
  persist(
    (set, get) => ({
      appliedVouchers: [],
      pendingVouchers: [],

      // Only allow 1 voucher - replaces existing
      addVoucher: (id, code, title) =>
        set(() => ({
          appliedVouchers: [{ id, code, title }],
        })),

      removeVoucher: (code) =>
        set((state) => ({
          appliedVouchers: state.appliedVouchers.filter((v) => v.code !== code),
        })),

      removeVouchers: (codes) =>
        set((state) => ({
          appliedVouchers: state.appliedVouchers.filter(
            (v) => !codes.includes(v.code)
          ),
        })),

      clearVouchers: () => set({ appliedVouchers: [] }),

      isVoucherApplied: (code) =>
        get().appliedVouchers.some((v) => v.code === code),

      getAppliedVoucher: () => get().appliedVouchers[0] ?? null,

      addPendingVoucher: (code) =>
        set((state) => {
          const pending = Array.isArray(state.pendingVouchers)
            ? state.pendingVouchers
            : [];
          return {
            pendingVouchers: pending.includes(code)
              ? pending
              : [...pending, code],
          };
        }),

      removePendingVoucher: (code) =>
        set((state) => {
          const pending = Array.isArray(state.pendingVouchers)
            ? state.pendingVouchers
            : [];
          return {
            pendingVouchers: pending.filter((v) => v !== code),
          };
        }),

      clearPendingVouchers: () => set({ pendingVouchers: [] }),

      isPendingVoucher: (code) => {
        const pending = get().pendingVouchers;
        return Array.isArray(pending) ? pending.includes(code) : false;
      },
    }),
    {
      name: 'voucher-storage',
      merge: (persistedState: unknown, currentState: VoucherState) => {
        const state = persistedState as Partial<VoucherState>;
        return {
          ...currentState,
          appliedVouchers: migrateVouchers(state?.appliedVouchers),
          pendingVouchers: Array.isArray(state?.pendingVouchers)
            ? state.pendingVouchers
            : [],
        };
      },
    }
  )
);
